import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import ReviewRespondentsForm from 'modules/reviews/lib/RespondentsFactorialForm'
import { canCreateReviews } from 'modules/reviews/lib/canCreateReviews'
import reviews from 'modules/reviews/store/collections/v2/reviews'
import type { IReview } from 'modules/reviews/store/models/v2/Review'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsManagingPeople } from 'modules/teams/store/userIsManagingPeople'

import Respondents from './Respondents'
import Target from './Target'

interface FooterProps {
  name: Field
  reviewForm: ReviewRespondentsForm
  loading: boolean
}

const Footer = observer(({ name, reviewForm, loading }: FooterProps) => {
  return (
    <Button disabled={!name.value || !reviewForm.valid() || loading} submit>
      {i18n.t('common.submit')}
    </Button>
  )
})

export const NewCustomPerformanceReview: React.FC<{
  onClose: () => void
  contractVersions: ContractVersions
}> = ({ onClose: onModalClose, contractVersions }) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const { hasFullVisibility } = usePerformanceModuleContext()
  const [loading, setLoading] = React.useState(false)

  const form = React.useRef(
    new Form(
      {
        name: null,
      },
      {
        name: 'string',
      }
    )
  ).current

  const reviewForm = React.useRef(
    new ReviewRespondentsForm({
      strategy: null,
      groupable_id: null,
      groupable_type: null,
    })
  ).current

  const onClose = () => {
    track(trackingEvents.reviews.createCancelReview)
    onModalClose()
  }

  const submit = async () => {
    setLoading(true)

    const data = reviewForm.data()
    const { success, value } = await asyncAction(() => {
      const payload = {
        name: form.get('name').value,
        type: 'review',
        target: {
          strategy: data.strategy,
          groupable_id: data.groupable_id,
          groupable_type: data.groupable_type,
          members: data.members,
        },
        respondents: {
          self: data.self,
          manager: data.manager,
          reportees: data.reportees,
          peers: data.peers,
          manual: data.manual,
          manual_members: data.manualMembers,
        },
      }

      return reviews.create(payload)
    })
    setLoading(false)

    track(trackingEvents.reviews.createSubmitReview, {
      is_successful: success,
      error_code: !success && JSON.stringify(value),
      reviewees: data.strategy,
      is_self_review: data.self,
      is_manager_reviewer: data.manager,
      has_direct_reports: data.reportees,
      is_peer_reviewed: data.peers,
      has_manual_reviewer: data.manual,
    })

    if (success) {
      navigate(
        navigation.performance.reviews.id.configuration.fullPath({ id: (value as IReview).id })
      )
    }
  }

  if (!canCreateReviews()) {
    return <Navigate to={navigation.performance.reviews.fullPath()}></Navigate>
  }

  const canSeeReviewTemplate =
    hasFullVisibility || userIsManagingPeople(teams, memberships, users.current.id)

  return (
    <Modal
      title={i18n.t('surveys.performance.new.title')}
      description={i18n.t('surveys.performance.new.description')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      progress={canSeeReviewTemplate ? { amount: 2, total: 2 } : undefined}
      footer={
        <Box aria-label="Action to create a review">
          <Footer name={form.get('name')} reviewForm={reviewForm} loading={loading} />
        </Box>
      }
    >
      <FormRow>
        <Box aria-label="Title of the review">
          <Text
            mandatory
            label={i18n.t('surveys.performance.new.form.labels.name')}
            field={form.get('name')}
          />
        </Box>
      </FormRow>
      <FormRow>
        <Box aria-label="All possible reviewees to be selectable">
          <Target reviewForm={reviewForm} contractVersions={contractVersions} />
        </Box>
      </FormRow>
      <FormRow>
        <Box aria-label="All possible respondents to be selectable">
          <Respondents
            respondentsForm={reviewForm.respondentsForm}
            contractVersions={contractVersions}
          />
        </Box>
      </FormRow>
    </Modal>
  )
}
